import React, { useState, useEffect, useCallback } from 'react'
import { IoClose } from "react-icons/io5";
import axios from 'axios';
import { API } from './constant';
import Select from 'react-select';
import CreatableSelect from 'react-select/creatable';
import { toast, ToastContainer } from 'react-toastify';

const MONTHS = [
  "April", "May", "June", "July", "August", "September",
  "October", "November", "December", "January", "February", "March"
];

const formatDate = (value) => {
  if (!value) return "N/A";
  const date = new Date(value);
  if (isNaN(date.getTime())) return "N/A";
  return date.toLocaleDateString();
};

const Invoices = () => {

  const [invoiceList, setInvoiceList] = useState([]);
  const [companyNameOptions, setCompanyNameOptions] = useState([]);
  const [sessionOptions, setSessionOptions] = useState([]);
  const [accountOptions, setAccountOptions] = useState([]);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  const [filters, setFilters] = useState({
    companyName: "",
    session: "",
    month: ""
  });

  const handleAuthError = (error) => {
    if (error.response?.status === 401 || error.response?.data?.message === "Invalid Token") {
      window.location.href = "/";
    }
  };

  const fetchInvoices = useCallback(async () => {
    try {
      const params = {};
      if (filters.companyName) params.companyName = filters.companyName;
      if (filters.session) params.session = filters.session;
      if (filters.month) params.month = filters.month;
      const response = await axios.get(`${API}/invoices`, { params });
      setInvoiceList(response.data);
    } catch (error) {
      console.error("Error fetching invoices:", error);
      handleAuthError(error);
    }
  }, [filters]);

  const fetchCompanyNames = async () => {
    try {
      const response = await axios.get(`${API}/account-details/company-names`);
      setCompanyNameOptions(response.data);
    } catch (error) {
      console.error("Error fetching company names:", error);
      handleAuthError(error);
    }
  };

  const fetchSessions = async () => {
    try {
      const response = await axios.get(`${API}/invoices/sessions`);
      setSessionOptions(response.data);
    } catch (error) {
      console.error("Error fetching sessions:", error);
      handleAuthError(error);
    }
  };

  const fetchAccounts = async () => {
    try {
      const response = await axios.get(`${API}/account-details`);
      setAccountOptions(response.data);
    } catch (error) {
      console.error("Error fetching accounts:", error);
      handleAuthError(error);
    }
  };

  useEffect(() => {
    fetchCompanyNames();
    fetchSessions();
    fetchAccounts();
  }, []);

  useEffect(() => {
    fetchInvoices();
  }, [fetchInvoices]);

  const handleUpload = async (formValues) => {
    try {
      const payload = new FormData();
      payload.append("accountId", formValues.accountId);
      payload.append("session", formValues.session);
      payload.append("month", formValues.month);
      payload.append("file", formValues.file);

      await axios.post(`${API}/invoices`, payload, {
        headers: { "Content-Type": "multipart/form-data" }
      });

      fetchInvoices();
      fetchSessions();
      setShowUploadModal(false);
      toast.success("Invoice uploaded");
    } catch (error) {
      if (error.response) {
        toast.warning(error.response.data.message || "Something went wrong")
      } else if (error.request) {
        toast.warning("Server not responding, please try again later")
      } else {
        toast.warning("Unexpected error occurred")
      }
      handleAuthError(error);
    }
  };

  const handleDownload = async (id) => {
    try {
      const response = await axios.get(`${API}/invoices/${id}/download`);
      window.open(response.data.url, "_blank");
    } catch (error) {
      toast.warning("Could not generate download link");
      handleAuthError(error);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this invoice?")) {
      try {
        await axios.delete(`${API}/invoices/${id}`);
        fetchInvoices();
      } catch (error) {
        console.error("Error deleting invoice:", error);
        handleAuthError(error);
      }
    }
  };

  const filteredInvoices = invoiceList.filter((invoice) => {
    const query = searchTerm.trim().toLowerCase();
    if (!query) return true;
    const applicationName = (invoice.applicationName || "").toLowerCase();
    const fileName = (invoice.fileName || "").toLowerCase();
    return applicationName.includes(query) || fileName.includes(query);
  });

  return (
    <div>
      <div>
        <ToastContainer autoClose={1000} />
        <div className='card_button_container'>
          <div className='button_search DFJCBAIC marginB10'>
            <div className='DFJCCAIC gap10'>
              <Select
                placeholder="Company"
                isClearable
                value={filters.companyName ? { value: filters.companyName, label: filters.companyName } : null}
                onChange={(option) => setFilters({ ...filters, companyName: option ? option.value : "" })}
                options={companyNameOptions.map((name) => ({ value: name, label: name }))}
                className="w-[160px] text-sm"
              />
              <Select
                placeholder="Session"
                isClearable
                value={filters.session ? { value: filters.session, label: filters.session } : null}
                onChange={(option) => setFilters({ ...filters, session: option ? option.value : "" })}
                options={sessionOptions.map((name) => ({ value: name, label: name }))}
                className="w-[140px] text-sm"
              />
              <Select
                placeholder="Month"
                isClearable
                value={filters.month ? { value: filters.month, label: filters.month } : null}
                onChange={(option) => setFilters({ ...filters, month: option ? option.value : "" })}
                options={MONTHS.map((name) => ({ value: name, label: name }))}
                className="w-[140px] text-sm"
              />
            </div>
            <div className='DFJCCAIC gap10'>
              <div>
                <button onClick={() => setShowUploadModal(true)} className='add_button'>Upload Invoice</button>
              </div>
              <div>
                <input
                  className='search_box'
                  type="search"
                  placeholder='Filter by application/file name'
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>
          </div>

          <hr />

          <div className='table_container width100'>
            <table className="mac_table">
              <thead>
                <tr>
                  <th>Company Name</th>
                  <th>Application Name</th>
                  <th>Session</th>
                  <th>Month</th>
                  <th>File Name</th>
                  <th>Uploaded On</th>
                  <th>Action</th>
                </tr>
              </thead>

              <tbody>
                {filteredInvoices.map((item) => (
                  <tr key={item._id}>
                    <td>{item.companyName}</td>
                    <td>{item.applicationName}</td>
                    <td>{item.session}</td>
                    <td>{item.month}</td>
                    <td>{item.fileName}</td>
                    <td>{formatDate(item.createdAt)}</td>
                    <td>
                      <button onClick={() => handleDownload(item._id)} style={{ marginRight: "10px", cursor: "pointer" }}>Download</button>
                      <button onClick={() => handleDelete(item._id)} style={{ cursor: "pointer", color: "red" }}>Delete</button>
                    </td>
                  </tr>
                ))}
                {filteredInvoices.length === 0 && (
                  <tr>
                    <td colSpan="7" style={{ textAlign: "center" }}>No invoices found.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {showUploadModal && (
            <UploadInvoiceModal
              onClose={() => setShowUploadModal(false)}
              onSubmit={handleUpload}
              accountOptions={accountOptions}
              sessionOptions={sessionOptions}
            />
          )}

        </div>
      </div>
    </div>
  )
}


const UploadInvoiceModal = ({ onClose, onSubmit, accountOptions = [], sessionOptions = [] }) => {

  const [accountId, setAccountId] = useState("");
  const [session, setSession] = useState("");
  const [month, setMonth] = useState("");
  const [file, setFile] = useState(null);
  const [takenMonths, setTakenMonths] = useState([]);

  useEffect(() => {
    const fetchTakenMonths = async () => {
      if (!accountId || !session) {
        setTakenMonths([]);
        return;
      }
      try {
        const response = await axios.get(`${API}/invoices`, { params: { accountId, session } });
        setTakenMonths(response.data.map((inv) => inv.month));
      } catch (error) {
        console.error("Error checking existing invoices:", error);
      }
    };
    fetchTakenMonths();
  }, [accountId, session]);

  useEffect(() => {
    if (month && takenMonths.includes(month)) {
      setMonth("");
    }
  }, [takenMonths, month]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!accountId || !session || !month || !file) {
      toast.warning("All fields are required.");
      return;
    }
    onSubmit({ accountId, session, month, file });
  };

  return (
    <div className="fixed inset-0 bg-black/30 flex justify-center items-center z-50">

      <div className="bg-white w-[520px] rounded-xl shadow-lg overflow-hidden">

        <div className="flex justify-between items-center px-4 py-3 border-b bg-[#f5f5f7]">
          <h2 className="text-sm font-medium">Upload Invoice</h2>
          <IoClose size={20} className="cursor-pointer" onClick={onClose} />
        </div>

        <form onSubmit={handleSubmit} className="p-4 space-y-3 text-sm max-h-[70vh] overflow-y-auto">

          <div className="form_group">
            <label>Account</label>
            <Select
              placeholder="Select company / application"
              value={
                accountId
                  ? (() => {
                      const acc = accountOptions.find((a) => a._id === accountId);
                      return acc ? { value: acc._id, label: `${acc.companyName} — ${acc.applicationName}` } : null;
                    })()
                  : null
              }
              onChange={(option) => setAccountId(option ? option.value : "")}
              options={accountOptions.map((acc) => ({ value: acc._id, label: `${acc.companyName} — ${acc.applicationName}` }))}
              className="w-full"
            />
          </div>

          <div className="form_group">
            <label>Session</label>
            <CreatableSelect
              placeholder="e.g. 2025-2026"
              value={session ? { value: session, label: session } : null}
              onChange={(option) => setSession(option ? option.value : "")}
              options={sessionOptions.map((name) => ({ value: name, label: name }))}
              className="w-full"
            />
          </div>

          <div className="form_group">
            <label>Month</label>
            <select
              value={month}
              onChange={(e) => setMonth(e.target.value)}
              className="w-full border rounded-md px-3 py-2"
            >
              <option value="">Select Month</option>
              {MONTHS.map((m) => (
                <option key={m} value={m} disabled={takenMonths.includes(m)}>
                  {m}{takenMonths.includes(m) ? " (already uploaded)" : ""}
                </option>
              ))}
            </select>
          </div>

          <div className="form_group">
            <label>Invoice File (PDF, JPG or PNG, up to 10MB)</label>
            <input
              type="file"
              accept=".pdf,.jpg,.jpeg,.png"
              onChange={(e) => setFile(e.target.files[0])}
              className="w-full border rounded-md px-3 py-2"
            />
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="white_button">Cancel</button>
            <button type="submit" className="add_button">Upload</button>
          </div>

        </form>
      </div>
    </div>
  );
};


export default Invoices
